<template>
  <div
    class="bg-neutral-50 px-3 pt-3 pb-1 border-b border-neutral-200 dark:border-slate-700"
  >
    <div class="flex flex-row justify-between items-center mb-4">
      <span
        class="text-base font-semibold break-words overflow-hidden whitespace-nowrap text-ellipsis text-gray-600 dark:text-slate-100 mb-0"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </span>
      <div class="flex flex-row items-center gap-1">
        <div
          v-if="hasInboxPermission('Inbox new message')"
          v-tooltip.right="$t('CONTACT_PANEL.NEW_MESSAGE')"
          class="button-ghost text-sm/[20px] px-3 py-[5px] flex flex-row items-center gap-[2px]"
          @click="toggleConversationModal"
        >
          <span class="material-symbols-outlined text-[16px]">add</span>
          <span class="whitespace-nowrap">{{ $t('NEW_CONVERSATION.SHORT_TITLE') }}</span>
        </div>
        <div>
          <span
            ref="chatListMoreOptionTrigger"
            class="material-symbols-outlined w-6 h-6 text-neutral-500 rounded-[4px] hover:bg-gray-200 cursor-pointer block"
            @click.stop="openMoreOptionDropdown"
          >
            more_vert
          </span>
          <div
            v-show="showMoreOptionDropdown"
            ref="chatListMoreOptionsDropdown"
            v-on-clickaway="closeDropdown"
            class="bg-white rounded-lg overflow-hidden"
            style="filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08))"
            :style="chatListMoreOptionStyle"
          >
            <div class="more-options-item" @click="markAllConversationsAsRead">
              <span class="material-symbols-outlined more-options-icon">
                checklist
              </span>
              <span class="more-options-label">
                {{ $t('CHAT_LIST.MARK_CONVERSATION_AS_READ') }}
              </span>
            </div>
            <div class="more-options-item" @click="openChecklist">
              <span class="material-symbols-outlined more-options-icon">
                check_box
              </span>
              <span class="more-options-label">
                {{ $t('CHAT_LIST.SELECT_CONVERSATIONS') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="isShowConversationCheckbox">
      <div
        class="flex flex-row items-center justify-between gap-2 w-full mt-4 mb-2"
      >
        <div class="flex flex-row items-center gap-2">
          <span
            class="material-symbols-outlined close-select-conversations"
            @click="hideConversationCheckbox"
          >
            close
          </span>
          <label class="bulk-action__panel flex-between">
            <input
              ref="selectAllCheck"
              type="checkbox"
              class="semantic-checkbox !mx-0"
              :checked="
                chatList.selectedConversations.length &&
                chatList.allConversationsSelected
              "
              :indeterminate.prop="
                chatList.selectedConversations.length &&
                !chatList.allConversationsSelected
              "
              @change="selectAllConversations($event)"
            />
          </label>
          <span
            v-if="chatList.selectedConversations.length"
            class="text-sm text-gray-600"
          >
            {{
              $t('BULK_ACTION.CONVERSATIONS_SELECTED', {
                conversationCount: chatList.selectedConversations.length,
              })
            }}
          </span>
        </div>
        <conversation-bulk-actions
          :conversations="chatList.selectedConversations"
          :all-conversations-selected="chatList.allConversationsSelected"
          :selected-inboxes="chatList.uniqueInboxes"
          :show-open-action="
            chatList.allSelectedConversationsStatus('active') /* d99d */
          "
          :show-resolved-action="
            chatList.allSelectedConversationsStatus('resolved')
          "
          :show-snoozed-action="
            chatList.allSelectedConversationsStatus('snoozed')
          "
          @select-all-conversations="chatList.selectAllConversations"
          @assign-agent="chatList.onAssignAgent"
          @update-conversations="chatList.onUpdateConversations"
          @assign-labels="chatList.onAssignLabels"
          @assign-team="chatList.onAssignTeamsForBulk"
        />
      </div>
      <div v-if="chatList.allConversationsSelected" class="bulk-action__alert">
        {{ $t('BULK_ACTION.ALL_CONVERSATIONS_SELECTED_ALERT') }}
      </div>
    </template>
    <div
      v-if="isShowFiltersActions"
      class="flex flex-row items-center justify-between flex-1 mb-2"
    >
      <div
        class="flex flex-row items-center justify-between gap-2"
        :class="isShowFiltersActions ? 'flex-row-reverse flex-1' : 'flex-row'"
      >
        <div v-if="hasAppliedFilters && !hasActiveFolders">
          <span
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            class="material-symbols-outlined text-base/[16px] text-[#6B7280] p-[7px] border border-[#E5E7EB] rounded-[4px] bg-white cursor-pointer"
            @click="onClickOpenAddFoldersModal"
          >
            save
          </span>
          <span
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            class="material-symbols-outlined text-base/[16px] text-[#B91C1C] p-[7px] border border-[#B91C1C] rounded-[4px] bg-[#FEF2F2] cursor-pointer"
            @click="resetAndFetchData"
          >
            cancel
          </span>
        </div>
        <div v-if="hasActiveFolders">
          <span
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            class="material-symbols-outlined text-base/[16px] text-[#6B7280] p-[7px] border border-[#E5E7EB] rounded-[4px] bg-white cursor-pointer"
            @click="onToggleAdvanceFiltersModal"
          >
            edit
          </span>
          <span
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            class="material-symbols-outlined text-base/[16px] text-[#B91C1C] p-[7px] border border-[#B91C1C1A] rounded-[4px] bg-[#FEF2F2] cursor-pointer"
            @click="onClickOpenDeleteFoldersModal"
          >
            delete
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="!isShowConversationCheckbox && !isShowFiltersActions"
      class="flex flex-row items-center mb-2 gap-1"
    >
      <router-link
        :to="searchUrl"
        class="button-ghost border-gray-300 flex-1 px-2 py-[6px] flex flex-row gap-1 items-center"
      >
        <span class="material-symbols-outlined text-base/4 text-gray-500">
          search
        </span>
        <p class="mb-0 text-sm text-gray-400 leading-[18px]">
          {{ $t('FILTER.SEARCH_PLACEHOLDER') }}
        </p>
      </router-link>
      <chat-order-by-selector
        v-if="!hasAppliedFiltersOrActiveFolders"
        @onSortbyChange="
          value => {
            chatList.activeSortBy = value;
            resetAndFetchData();
          }
        "
      />
      <div
        v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
        class="button-ghost p-[7px] rounded-[4px] flex flex-row cursor-pointer hover:bg-gray-50 hover:border-gray-200"
        @click="onToggleAdvanceFiltersModal"
      >
        <span
          class="material-symbols-outlined text-gray-500 text-base/4 !font-normal"
        >
          filter_alt
        </span>
      </div>
    </div>
    <chat-type-tabs
      v-if="hasAppliedFiltersOrActiveFolders || !isShowConversationCheckbox"
      :active-tab="
        hasAppliedFiltersOrActiveFolders
          ? chatList.activeAssigneeTab
          : chatList.activeTab
      "
      :disabled-switch-group="hasAppliedFiltersOrActiveFolders"
      @update-tab="chatList.updateTab"
    />
    <new-fb-post
      v-if="isAFacebook2Inbox"
      :show.sync="showNewConversationModal"
      :inbox="inbox"
    />
    <new-contact-and-conversation
      v-else
      :show.sync="showNewConversationModal"
      :inbox="inbox"
      :is-single-window="false"
      :hide-empty-state="true"
    />
    <add-custom-views
      v-if="showAddFoldersModal"
      :custom-views-query="foldersQuery"
      :open-last-saved-item="openLastSavedItemInFolder"
      @close="onCloseAddFoldersModal"
    />
    <delete-custom-views
      v-if="showDeleteFoldersModal"
      :show-delete-popup.sync="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />
    <woot-modal
      :show.sync="showAdvancedFilters"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
      class="conversation-advanced-filter-modal"
    >
      <conversation-advanced-filter
        v-if="showAdvancedFilters"
        :initial-filter-types="advancedFilterTypes"
        :initial-applied-filters="appliedFilter"
        :active-folder-name="activeFolderName"
        :on-close="closeAdvanceFiltersModal"
        :is-folder-view="hasActiveFolders"
        @applyFilter="onApplyFilter"
        @updateFolder="onUpdateSavedFilter"
      />
    </woot-modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import ChatTypeTabs from './ChatTypeTabs.vue';
import ChatOrderBySelector from './ChatOrderBySelector.vue';
import ConversationBulkActions from '../ConversationBulkActions/Index.vue';
import NewContactAndConversation from 'dashboard/routes/onlineWaOnlyConversation/NewContactAndConversation.vue';
import NewFbPost from 'dashboard/routes/onlineFbNewConversation/NewFbPost.vue';
import AddCustomViews from 'dashboard/routes/dashboard/customviews/AddCustomViews.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationAdvancedFilter from 'dashboard/components/widgets/conversation/ConversationAdvancedFilter.vue';

import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import filterMixin from 'shared/mixins/filterMixin';
import inboxMixin from 'shared/mixins/inboxMixin.js';

import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';
import { mixin as clickaway } from 'vue-clickaway';
import rtlMixin from 'shared/mixins/rtlMixin';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { CONVERSATION_LIST_TAB_GROUPS } from 'dashboard/mixins/uiSettings';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import advancedFilterTypes from 'dashboard/components/widgets/conversation/advancedFilterItems/index.js';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';

export default {
  components: {
    ChatTypeTabs,
    ChatOrderBySelector,
    NewFbPost,
    NewContactAndConversation,
    AddCustomViews,
    ConversationAdvancedFilter,
    DeleteCustomViews,
    ConversationBulkActions,
  },
  mixins: [hasPermissionMixin, filterMixin, inboxMixin, clickaway, rtlMixin],
  inject: [
    'chatList',
    'displayConversationCheckbox',
    'hideConversationCheckbox',
    'isShowConversationCheckbox',
  ],
  data() {
    return {
      showNewConversationModal: false,
      showAddFoldersModal: false,
      showDeleteFoldersModal: false,
      showAdvancedFilters: false,
      appliedFilter: [],
      foldersQuery: {},
      advancedFilterTypes: advancedFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
      })),
      isOnExpandedLayout: false,
      chatListMoreOptionStyle: null,
      showMoreOptionDropdown: false,
    };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      folders: 'customViews/getCustomViews',
      agentList: 'agents/getAgents',
      teamsList: 'teams/getTeams',
      inboxesList: 'inboxes/getInboxes',
      campaigns: 'campaigns/getAllCampaigns',
      labels: 'labels/getLabels',
      accountId: 'getCurrentAccountId',
    }),
    searchUrl() {
      return frontendURL(`accounts/${this.accountId}/search`);
    },
    foldersId() {
      return this.chatList.foldersId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.activeInbox);
    },
    hasAppliedFiltersOrActiveFolders() {
      return this.chatList.hasAppliedFiltersOrActiveFolders;
    },
    hasAppliedFilters() {
      return this.chatList.hasAppliedFilters;
    },
    hasActiveFolders() {
      return this.chatList.hasActiveFolders;
    },
    activeFolder() {
      return this.chatList.activeFolder;
    },
    activeFolderName() {
      return this.activeFolder?.name;
    },
    activeStatus() {
      return this.chatList.activeStatus;
    },
    activeAssigneeTab() {
      return this.chatList.effectiveAssigneeTab;
    },
    currentUserDetails() {
      return this.chatList.currentUserDetails;
    },
    conversationInbox() {
      return this.chatList.conversationInbox;
    },
    teamId() {
      return this.chatList.teamId;
    },
    activeTeam() {
      return this.chatList.activeTeam;
    },
    label() {
      return this.chatList.label;
    },
    pageTitle() {
      if (this.hasAppliedFilters) {
        return `${this.$t('CHAT_LIST.TAB_HEADING')} (${
          this.chatList.conversationStats.allCount
        })`;
      }
      if (this.inbox.name) {
        const isSMS = this.inbox.channel_type === 'Channel::Sms';
        const phone =
          this.inbox.phone_number || this.inbox.channel?.phone_number || '';
        return isSMS && phone
          ? `${this.inbox.name} (0${phone.slice(3)})`
          : this.inbox.name;
      }
      if (this.chatList.activeTeam.name) {
        return this.chatList.activeTeam.name;
      }

      if (this.chatList.label) {
        return `#${this.label}`;
      }
      if (this.chatList.conversationType === 'mention') {
        return this.$t('CHAT_LIST.MENTION_HEADING');
      }
      if (this.chatList.conversationType === 'participating') {
        return this.$t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
      }
      if (this.chatList.conversationType === 'unattended') {
        return this.$t('CHAT_LIST.UNATTENDED_HEADING');
      }
      if (this.hasActiveFolders) {
        return `${this.activeFolder.name} (${this.chatList.conversationStats.allCount})`;
      }
      return this.$t('CHAT_LIST.TAB_HEADING');
    },
    isShowFiltersActions() {
      return (
        (this.hasAppliedFilters && !this.hasActiveFolders) ||
        this.hasActiveFolders
      );
    },
  },
  mounted() {
    if (this.hasActiveFolders || this.hasAppliedFilters) {
      this.chatList.updateConversationListTabGroup(
        CONVERSATION_LIST_TAB_GROUPS.OWNERSHIP
      );
    }
    bus.$on(BUS_EVENTS.START_NEW_CONVERSATION, this.toggleConversationModal);
  },
  destroyed() {
    bus.$off(BUS_EVENTS.START_NEW_CONVERSATION, this.toggleConversationModal);
  },
  methods: {
    selectAllConversations(e) {
      this.chatList.selectAllConversations(e.target.checked);
    },
    toggleConversationModal() {
      this.showNewConversationModal = !this.showNewConversationModal;
    },
    onClickOpenAddFoldersModal() {
      this.showAddFoldersModal = true;
    },
    onCloseAddFoldersModal() {
      this.showAddFoldersModal = false;
    },
    onClickOpenDeleteFoldersModal() {
      this.showDeleteFoldersModal = true;
    },
    onCloseDeleteFoldersModal() {
      this.showDeleteFoldersModal = false;
    },
    resetAndFetchData() {
      this.appliedFilter = [];
      this.chatList.resetAndFetchData();
    },
    onToggleAdvanceFiltersModal() {
      if (!this.hasAppliedFilters && !this.hasActiveFolders) {
        this.initializeExistingFilterToModal();
      }
      if (this.hasActiveFolders) {
        this.initializeFolderToFilterModal(this.activeFolder);
      }
      this.showAdvancedFilters = true;
    },
    closeAdvanceFiltersModal() {
      this.showAdvancedFilters = false;
      this.appliedFilter = [];
    },
    onApplyFilter(payload) {
      this.chatList.resetBulkActions();
      this.foldersQuery = filterQueryGenerator(payload);
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.chatList.fetchFilteredConversations(payload);
      this.chatList.updateConversationListTabGroup(
        CONVERSATION_LIST_TAB_GROUPS.OWNERSHIP
      );
      this.closeAdvanceFiltersModal();
    },
    onUpdateSavedFilter(payload, folderName) {
      const payloadData = {
        ...this.activeFolder,
        name: folderName,
        query: filterQueryGenerator(payload),
      };
      this.$store.dispatch('customViews/update', payloadData);
      this.closeAdvanceFiltersModal();
    },
    openLastSavedItemInFolder() {
      const lastItemOfFolder = this.folders[this.folders.length - 1];
      const lastItemId = lastItemOfFolder.id;
      this.$router.push({
        name: 'folder_conversations',
        params: { id: lastItemId },
      });
    },
    openLastItemAfterDeleteInFolder() {
      if (this.folders.length > 0) {
        this.openLastSavedItemInFolder();
      } else {
        this.$router.push({ name: 'home' });
        this.chatList.fetchConversations();
      }
    },
    initializeFolderToFilterModal(activeFolder) {
      // Here we are setting the params for edit folder modal.
      //  To show the existing values. when we click on edit folder button.

      // Here we get the query from the active folder.
      // And we are mapping the query to the actual values.
      // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
      const query = activeFolder?.query?.payload;
      if (!Array.isArray(query)) return;

      this.appliedFilter.push(
        ...query.map(filter => ({
          attribute_key: filter.attribute_key,
          attribute_model: filter.attribute_model,
          filter_operator: filter.filter_operator,
          values: Array.isArray(filter.values)
            ? generateValuesForEditCustomViews(
                filter,
                this.setParamsForEditFolderModal()
              )
            : [],
          query_operator: filter.query_operator,
          custom_attribute_type: filter.custom_attribute_type,
        }))
      );
    },
    setParamsForEditFolderModal() {
      // Here we are setting the params for edit folder modal to show the existing values.

      // For agent, team, inboxes,and campaigns we get only the id's from the query.
      // So we are mapping the id's to the actual values.

      // For labels we get the name of the label from the query.
      // If we delete the label from the label list then we will not be able to show the label name.

      // For custom attributes we get only attribute key.
      // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
      const params = {
        agents: this.agentList,
        teams: this.teamsList,
        inboxes: this.inboxesList,
        labels: this.labels,
        campaigns: this.campaigns,
        languages: languages,
        countries: countries,
        filterTypes: advancedFilterTypes,
        allCustomAttributes: this.$store.getters[
          'attributes/getAttributesByModel'
        ]('conversation_attribute'),
      };
      return params;
    },
    openMoreOptionDropdown() {
      if (!this.chatListMoreOptionStyle)
        this.calculateMoreOptionDropdownPosition();
      this.showMoreOptionDropdown = !this.showMoreOptionDropdown;
    },
    closeDropdown() {
      this.showMoreOptionDropdown = false;
    },
    calculateMoreOptionDropdownPosition() {
      const triggerEl = this.$refs.chatListMoreOptionTrigger;
      const dropdownEl = this.$refs.chatListMoreOptionsDropdown;
      const rect = dropdownEl.getBoundingClientRect();

      this.chatListMoreOptionStyle = getFixedDialogStyle(triggerEl, {
        width: this.isRTLView ? rect.width + 138 : rect.width,
        height: rect.height,
        gap: 8,
        align: this.isRTLView ? 'right' : 'left',
        preferredPlacement: 'bottom',
      });
      this.chatListMoreOptionStyle.zIndex = 20;
    },
    markAllConversationsAsRead() {
      this.chatList.onMarkAsReadAll();
      this.showMoreOptionDropdown = false;
    },
    openChecklist() {
      this.displayConversationCheckbox();
      this.showMoreOptionDropdown = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.bulk-action__panel {
  @apply cursor-pointer;

  span {
    @apply text-xs my-0 mx-1;
  }

  input[type='checkbox'] {
    @apply cursor-pointer m-0;
  }
}

.more-options-item {
  @apply flex items-center gap-1 px-2 py-1 cursor-pointer;

  .more-options-icon {
    @apply text-base/4 text-gray-500;
  }

  .more-options-label {
    @apply text-gray-600 text-sm;
  }

  &:hover {
    @apply bg-green-50;

    .more-options-icon {
      @apply text-[#1F845A];
    }

    .more-options-label {
      @apply text-[#164B35];
    }
  }
}

.close-select-conversations {
  @apply text-neutral-500 w-6 h-6 flex items-center justify-center text-[20px]/[20px] cursor-pointer;
  @apply rounded-[4px] hover:bg-gray-200;
}

.bulk-action__alert {
  @apply bg-yellow-50 text-[#B45309] border border-solid border-yellow-100 rounded;
  @apply mb-2 text-sm/4 px-2 py-3 dark:border-yellow-300/10 dark:bg-yellow-200/20 dark:text-yellow-400;
}
</style>
